import {forwardRef, Ref, useCallback, useEffect, useId, useState} from 'react';
import './schedule-field.scss';
import {useTranslation} from 'react-i18next';
import {FormControl, TextField, Box, InputAdornment, IconButton} from '@mui/material';
import FlatInput, {FlatInputProps} from '../flat-input/flat-input';
import {FiEdit} from 'react-icons/fi';
import {Pallete} from '../../theme/bluebird';
import {translations} from '../../i18n/translations';
import {scheduleReadableExpression} from '../../utils/functions';

export type ScheduleFieldProps = FlatInputProps & {
  openSchedule: () => void;
  readonly?: boolean;
  disabledRepetition?: boolean;
};

export const ScheduleField = forwardRef(
  (
    {
      required,
      label,
      type,
      errors,
      openSchedule,
      disabled,
      disabledTooltip,
      readonly,
      disabledRepetition,
      parentFormInstance, // destructure to remove from ...rest
      ...rest
    }: ScheduleFieldProps & {parentFormInstance?: any},
    ref: Ref<HTMLInputElement>
  ) => {
    const {t} = useTranslation();

    const [displayRepetitionValue, setDisplayRepetitionValue] = useState(
      rest.id === '65247958bcf63b665da3f3f6' // posology
        ? t(translations.schedule_field.readable_periodicity.not_defined)
        : t(translations.schedule_field.readable_periodicity.no_repetition)
    );

    const id = useId();

    const uniqueId = rest.id ?? `schedule_input_${id}`;

    const handleUpdateDisplay = useCallback(
      (value: string | unknown) => {
        // create expression
        const expression = scheduleReadableExpression({
          t,
          scheduleDefinition: JSON.parse(String(value)),
        });

        if (disabledRepetition) {
          // If disabled/readonly, set default disabled expression
          setDisplayRepetitionValue(
            t(translations.schedule_field.readable_periodicity.defined_per_activity)
          );
        } else {
          // Read expression and set display value
          setDisplayRepetitionValue(expression);
        }
      },
      [disabledRepetition, t]
    );

    useEffect(() => {
      rest.value && handleUpdateDisplay(rest.value);
    }, [handleUpdateDisplay, rest.value]);

    return (
      <Box
        className="schedule-modal"
        style={{width: '100%', opacity: disabledRepetition && !readonly ? 0.4 : 1}}
      >
        <FormControl
          sx={{width: '100%'}}
          onClick={disabledRepetition || disabled || readonly ? undefined : openSchedule}
        >
          {/* display */}
          <FlatInput
            label={label}
            required={required}
            value={displayRepetitionValue}
            aria-readonly="true"
            readonly={readonly || disabledRepetition}
            disabled={disabled || disabledRepetition}
            disabledTooltip={disabledTooltip}
            InputProps={{
              onChange: rest.onChange,
              onBlur: rest.onBlur,
              readOnly: readonly,
              sx: {
                cursor: 'pointer',
                input: {
                  fontWeight: 500,
                },
              },
              endAdornment: (
                <InputAdornment position="end" disablePointerEvents={disabled}>
                  {!disabledRepetition && !readonly && (
                    <IconButton sx={{width: '32px', height: '32px'}} onClick={openSchedule}>
                      <FiEdit fontSize="19px" color={Pallete.gray.base} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            errors={errors}
          />

          {/* actual value */}
          <TextField id={uniqueId} ref={ref} sx={{display: 'none'}} {...rest} />
        </FormControl>
      </Box>
    );
  }
);
